// client/src/components/PromptEdit.js
// Prompt creation and editing component for superadmins

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { HeadlineMedium, LabelMedium } from './Typography';
import { db } from '../firebase';
import { doc, getDoc, setDoc, addDoc, collection } from 'firebase/firestore';
import Toast from './Toast';

const PromptEdit = ({ onBackToPrompts }) => {
  const { promptId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Toast notification state
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    body: '',
    category: '',
    length: ''
  });


  // Toast helper functions
  const showToast = (message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setToastVisible(true);
  };

  const hideToast = () => {
    setToastVisible(false);
  };

  // Load prompt data
  useEffect(() => {
    const loadPromptData = async () => {
      if (!promptId) {
        // Creating new prompt - set default values
        setFormData({
          title: '',
          body: '',
          category: '',
          length: ''
        });
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);

        // Fetch existing prompt details
        const promptRef = doc(db, 'prompts', promptId);
        const promptSnapshot = await getDoc(promptRef);
        
        if (promptSnapshot.exists()) {
          const promptData = promptSnapshot.data();
          setFormData({
            title: promptData.title || '',
            body: promptData.body || promptData.description || '',
            category: promptData.category || '',
            length: promptData.length || ''
          });
        } else {
          showToast('Prompt not found', 'error');
        }

      } catch (error) {
        console.error('Error loading prompt data:', error);
        showToast('Failed to load prompt data', 'error');
      } finally {
        setLoading(false);
      }
    };

    loadPromptData();
  }, [promptId]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Rich text editor functions
  const applyFormat = (field, format) => {
    const textarea = document.getElementById(`prompt-${field}`);
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);
    
    let formattedText = '';
    switch (format) {
      case 'bold':
        formattedText = `**${selectedText}**`;
        break;
      case 'italic':
        formattedText = `*${selectedText}*`;
        break;
      case 'underline':
        formattedText = `__${selectedText}__`;
        break;
      default:
        formattedText = selectedText;
    }
    
    const newValue = textarea.value.substring(0, start) + formattedText + textarea.value.substring(end);
    handleInputChange(field, newValue);
    
    // Set cursor position after formatting
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + formattedText.length, start + formattedText.length);
    }, 0);
  };


  const handleSave = async () => {
    try {
      setSaving(true);

      // Prepare prompt data
      const promptData = {
        title: formData.title.trim(),
        body: formData.body.trim(),
        category: formData.category,
        length: formData.length,
        updatedAt: new Date().toISOString()
      };

      if (promptId) {
        // Update existing prompt
        const promptRef = doc(db, 'prompts', promptId);
        await setDoc(promptRef, promptData, { merge: true });
        showToast('Prompt updated successfully!', 'success');
      } else {
        // Create new prompt
        promptData.createdAt = new Date().toISOString();
        await addDoc(collection(db, 'prompts'), promptData);
        showToast('Prompt created successfully!', 'success');
      }

    } catch (error) {
      console.error('Error saving prompt:', error);
      showToast('Failed to save prompt', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading prompt...</span>
      </div>
    );
  }

  const pageTitle = promptId ? 'Prompt Edit' : 'Create New Prompt';
  const saveButtonText = saving ? 'Saving...' : (promptId ? 'Save Changes' : 'Create Prompt');

  return (
    <div className="space-y-8">
      {/* Toast Notification */}
      <Toast
        message={toastMessage}
        type={toastType}
        isVisible={toastVisible}
        onClose={hideToast}
        duration={3000}
      />

      {/* Back Button */}
      {onBackToPrompts && (
        <div className="mb-4">
          <button
            onClick={onBackToPrompts}
            className="flex items-center text-blue-600 hover:text-blue-800 transition-colors"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Prompts
          </button>
        </div>
      )}

      {/* Page Header */}
      <div className="flex items-center justify-between">
        <HeadlineMedium color="gray-900">{pageTitle}</HeadlineMedium>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          {saveButtonText}
        </button>
      </div>

      {/* Main Content */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="space-y-6">
          {/* Prompt Title */}
          <div>
            <LabelMedium color="gray-700">Prompt Title</LabelMedium>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter prompt title"
            />
          </div>

          {/* Prompt Body */}
          <div>
            <LabelMedium color="gray-700">Prompt Instructions</LabelMedium>
            <div className="mt-1">
              {/* Rich Text Toolbar */}
              <div className="flex space-x-2 mb-2 p-2 bg-gray-50 border border-gray-300 rounded-t-md">
                <button
                  type="button"
                  onClick={() => applyFormat('body', 'bold')}
                  className="px-3 py-1 text-sm font-bold bg-white border border-gray-300 rounded hover:bg-gray-100"
                  title="Bold"
                >
                  B
                </button>
                <button
                  type="button"
                  onClick={() => applyFormat('body', 'italic')}
                  className="px-3 py-1 text-sm italic bg-white border border-gray-300 rounded hover:bg-gray-100"
                  title="Italic"
                >
                  I
                </button>
                <button
                  type="button"
                  onClick={() => applyFormat('body', 'underline')}
                  className="px-3 py-1 text-sm underline bg-white border border-gray-300 rounded hover:bg-gray-100"
                  title="Underline"
                >
                  U
                </button>
              </div>
              
              {/* Text Area */}
              <textarea
                id="prompt-body"
                value={formData.body}
                onChange={(e) => handleInputChange('body', e.target.value)}
                rows={6}
                className="block w-full border border-gray-300 rounded-b-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter prompt instructions (use B/I/U buttons above to format selected text)"
              />
            </div>
          </div>

          {/* Category and Length Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Category */}
            <div>
              <LabelMedium color="gray-700">Category</LabelMedium>
              <select
                value={formData.category}
                onChange={(e) => handleInputChange('category', e.target.value)}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a category</option>
                <option value="Revealing Characters">Revealing Characters</option>
                <option value="Literary Devices">Literary Devices</option>
                <option value="Showing vs. Telling">Showing vs. Telling</option>
                <option value="Setting">Setting</option>
                <option value="Scene Construction">Scene Construction</option>
                <option value="Dialogue">Dialogue</option>
                <option value="Hooks">Hooks</option>
                <option value="Scene Endings">Scene Endings</option>
              </select>
            </div>

            {/* Length */}
            <div>
              <LabelMedium color="gray-700">Length</LabelMedium>
              <select
                value={formData.length}
                onChange={(e) => handleInputChange('length', e.target.value)}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a length</option>
                <option value="5 minutes">5 minutes</option>
                <option value="10 minutes">10 minutes</option>
                <option value="20 minutes">20 minutes</option>
              </select>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default PromptEdit;
